import React, { useState } from 'react';
import { X, User, Phone, Mail, MapPin, Calendar, Package, FileText, CheckCircle, XCircle, Clock } from 'lucide-react';
import { format } from 'date-fns';

export default function ReservationDetailModal({ reservation, onClose, onUpdateStatus, onRefresh }) {
  const [activeTab, setActiveTab] = useState('details');
  const [statusNotes, setStatusNotes] = useState('');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);

  const handleStatusChange = (status) => {
    setSelectedStatus(status);
    setShowStatusModal(true);
  };

  const handleStatusConfirm = async () => {
    if (selectedStatus) {
      await onUpdateStatus(reservation?.id, selectedStatus, statusNotes);
      setShowStatusModal(false);
      setSelectedStatus(null);
      setStatusNotes('');
      onRefresh();
      onClose();
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'confirmed': return 'text-green-600 bg-green-100';
      case 'processing': return 'text-blue-600 bg-blue-100';
      case 'completed': return 'text-purple-600 bg-purple-100';
      case 'cancelled': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'En Attente';
      case 'confirmed': return 'Confirmée';
      case 'processing': return 'En Traitement';
      case 'completed': return 'Terminée';
      case 'cancelled': return 'Annulée';
      default: return status;
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{reservation?.customer_name}</h2>
              <p className="text-sm text-gray-600">Réservation du {format(new Date(reservation?.created_at), 'dd/MM/yyyy à HH:mm')}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 px-6">
            <div className="flex space-x-4">
              <button
                onClick={() => setActiveTab('details')}
                className={`px-4 py-3 font-medium border-b-2 transition-colors ${
                  activeTab === 'details' ?'border-green-600 text-green-600' :'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Détails
              </button>
              <button
                onClick={() => setActiveTab('status')}
                className={`px-4 py-3 font-medium border-b-2 transition-colors ${
                  activeTab === 'status' ?'border-green-600 text-green-600' :'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Gestion Statut
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {activeTab === 'details' && (
              <div className="space-y-6">
                {/* Status Badge */}
                <div className="flex items-center justify-between">
                  <span className={`px-4 py-2 rounded-full text-sm font-semibold ${getStatusColor(reservation?.status)}`}>
                    {getStatusLabel(reservation?.status)}
                  </span>
                  {reservation?.confirmation_sent && (
                    <div className="flex items-center space-x-2 text-green-600">
                      <CheckCircle size={20} />
                      <span className="text-sm font-medium">Confirmation envoyée</span>
                    </div>
                  )}
                </div>

                {/* Customer Information */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                    <User size={20} />
                    <span>Informations Client</span>
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <div className="flex items-center space-x-2">
                      <Phone size={16} className="text-gray-500" />
                      <span className="text-gray-900">{reservation?.customer_phone}</span>
                    </div>
                    {reservation?.customer_email && (
                      <div className="flex items-center space-x-2">
                        <Mail size={16} className="text-gray-500" />
                        <span className="text-gray-900">{reservation?.customer_email}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Pickup Information */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                    <MapPin size={20} />
                    <span>Adresse de Collecte</span>
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-900">{reservation?.pickup_address}</p>
                    {reservation?.pickup_city && (
                      <p className="text-gray-600 mt-1">{reservation?.pickup_city} {reservation?.pickup_postal_code}</p>
                    )}
                  </div>
                </div>

                {/* Delivery Information */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                    <MapPin size={20} />
                    <span>Adresse de Livraison</span>
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-900">{reservation?.delivery_address}</p>
                    {reservation?.delivery_city && (
                      <p className="text-gray-600 mt-1">{reservation?.delivery_city}, {reservation?.delivery_country}</p>
                    )}
                  </div>
                </div>

                {/* Service Details */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                    <Package size={20} />
                    <span>Détails du Service</span>
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    {reservation?.collection_date && (
                      <div className="flex items-center space-x-2">
                        <Calendar size={16} className="text-gray-500" />
                        <span className="text-gray-900">Collecte: {format(new Date(reservation?.collection_date), 'dd/MM/yyyy')}</span>
                      </div>
                    )}
                    {reservation?.service_type && (
                      <div>
                        <span className="text-gray-600 font-medium">Type: </span>
                        <span className="text-gray-900">{reservation?.service_type}</span>
                      </div>
                    )}
                    {reservation?.item_description && (
                      <div>
                        <span className="text-gray-600 font-medium">Description: </span>
                        <span className="text-gray-900">{reservation?.item_description}</span>
                      </div>
                    )}
                    {reservation?.special_instructions && (
                      <div>
                        <span className="text-gray-600 font-medium">Instructions: </span>
                        <span className="text-gray-900">{reservation?.special_instructions}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Admin Notes */}
                {reservation?.admin_notes && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                      <FileText size={20} />
                      <span>Notes Admin</span>
                    </h3>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-gray-900">{reservation?.admin_notes}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'status' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Changer le Statut</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    onClick={() => handleStatusChange('confirmed')}
                    disabled={reservation?.status === 'confirmed'}
                    className="flex items-center justify-center space-x-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <CheckCircle size={20} />
                    <span>Confirmer</span>
                  </button>
                  <button
                    onClick={() => handleStatusChange('processing')}
                    disabled={reservation?.status === 'processing'}
                    className="flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Clock size={20} />
                    <span>En Traitement</span>
                  </button>
                  <button
                    onClick={() => handleStatusChange('completed')}
                    disabled={reservation?.status === 'completed'}
                    className="flex items-center justify-center space-x-2 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <CheckCircle size={20} />
                    <span>Terminée</span>
                  </button>
                  <button
                    onClick={() => handleStatusChange('cancelled')}
                    disabled={reservation?.status === 'cancelled'}
                    className="flex items-center justify-center space-x-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <XCircle size={20} />
                    <span>Annuler</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Status Change Confirmation Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirmer le Changement de Statut</h3>
            <p className="text-gray-600 mb-4">Notes optionnelles :</p>
            <textarea
              value={statusNotes}
              onChange={(e) => setStatusNotes(e?.target?.value)}
              rows={3}
              placeholder="Ajouter des notes..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent mb-4"
            />
            <div className="flex items-center space-x-3">
              <button
                onClick={handleStatusConfirm}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Confirmer
              </button>
              <button
                onClick={() => {
                  setShowStatusModal(false);
                  setSelectedStatus(null);
                  setStatusNotes('');
                }}
                className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}